import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import MarkdownIt from 'markdown-it';
import MdEditor from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';
import { FormInputContainer } from 'blaze-form';
import RenderMd from '../../../util/markdown';

const defaultPlugins = [
  'header',
  'font-bold',
  'font-italic',
  'font-underline',
  'font-strikethrough',
  'block-quote',
  'table',
  'link',
  'mode-toggle',
];

const FormInputMarkdown = ({ controlId, onChange, onBlur, field, ...props }) => {
  const [value, setValue] = useState(Array.isArray(props.value) ? props.value.join('\n') : props.value);
  const mdParser = new MarkdownIt();
  const firstUpdate = useRef(true);
  const consumerUpdate = useRef(false);

  const handleEditorChange = ({ text }) => {
    setValue(text);
  };

  const handleEditorBlur = () => {
    onBlur(field.name);
  };

  const handleUpdate = payload => {
    if (payload.value !== undefined) {
      consumerUpdate.current = true;
      setValue(payload.value);
    }
  };

  const handleClear = () => {
    setValue('');
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    onChange(field.name, value);

    if (consumerUpdate.current) {
      onBlur(field.name);
    }
  }, [value]);

  useEffect(() => {
    consumerUpdate.current = false;
  });

  const readOnly = field.readOnly || field.displayOnly;
  const view = readOnly ? { menu: false, md: false, html: true } : { menu: true, md: true, html: true };
  const className = classNames({ 'is-empty': value === '' });

  if (field.displayOnly) {
    return (
      <FormInputContainer
        {...props}
        className={className}
        controlId={controlId}
        field={field}
        onUpdate={handleUpdate}
        onClear={handleClear}
      >
        <RenderMd className="custom-html-style" md={value} />
      </FormInputContainer>
    );
  }

  return (
    <FormInputContainer
      {...props}
      className={className}
      controlId={controlId}
      field={field}
      onUpdate={handleUpdate}
      onClear={handleClear}
    >
      <MdEditor
        id={controlId}
        value={value}
        readOnly={readOnly}
        config={{ markdownClass: 'form-control', view, canView: { hideMenu: !readOnly, md: true, html: true } }}
        style={{ height: readOnly ? 'auto' : '250px' }}
        plugins={readOnly ? [] : defaultPlugins}
        renderHTML={text => mdParser.render(text)}
        onChange={handleEditorChange}
        onBlur={handleEditorBlur}
      />
    </FormInputContainer>
  );
};

FormInputMarkdown.propTypes = {
  controlId: PropTypes.string,
  field: PropTypes.object,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export default FormInputMarkdown;
